import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Bell, ChevronDown, Moon, Sun, Globe, LogOut,
  Sparkles, Menu, X
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import BrandLockup, { BrandMark } from './brand/BrandLockup';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { authClient } from '../lib/authClient';
import {
  TOPBAR_NAV_MENU_LOCK_EVENT,
  TOPBAR_NAV_MENU_UNLOCK_EVENT,
  TOPBAR_NAV_MENU_OPEN_EVENT,
} from './tour/tourElementUtils';

export default function TopBar() {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useCurrentUser();

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Account dropdown state
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  const accountMenuRef = useRef(null);
  const navMenuLockedRef = useRef(false);

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const nextLang = localStorage.getItem('language') || 'english';
      setLanguage(nextLang);
      document.documentElement.dir = nextLang === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = nextLang === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', newLanguage);
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    window.dispatchEvent(new Event('languageChange'));
  };

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname]);

  // Guided tour events
  useEffect(() => {
    const openMenu = () => setIsMobileMenuOpen(true);
    const closeMenu = () => setIsMobileMenuOpen(false);
    window.addEventListener('openMobileMenu', openMenu);
    window.addEventListener('closeMobileMenu', closeMenu);
    return () => {
      window.removeEventListener('openMobileMenu', openMenu);
      window.removeEventListener('closeMobileMenu', closeMenu);
    };
  }, []);

  useEffect(() => {
    const lock = () => {
      navMenuLockedRef.current = true;
    };
    const unlock = () => {
      navMenuLockedRef.current = false;
    };
    window.addEventListener(TOPBAR_NAV_MENU_LOCK_EVENT, lock);
    window.addEventListener(TOPBAR_NAV_MENU_UNLOCK_EVENT, unlock);
    return () => {
      window.removeEventListener(TOPBAR_NAV_MENU_LOCK_EVENT, lock);
      window.removeEventListener(TOPBAR_NAV_MENU_UNLOCK_EVENT, unlock);
    };
  }, []);

  useEffect(() => {
    const openNav = (e) => {
      const kind = e?.detail?.kind;
      if (kind === 'company' || kind === 'management') {
        navMenuLockedRef.current = true;
        if (window.innerWidth < 1024) {
          setIsMobileMenuOpen(true);
        }
      }
    };
    window.addEventListener(TOPBAR_NAV_MENU_OPEN_EVENT, openNav);
    return () => window.removeEventListener(TOPBAR_NAV_MENU_OPEN_EVENT, openNav);
  }, []);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Translations
  const t = (en, he) => (language === 'hebrew' ? he : en);

  const isManagerOrAdmin =
    currentUser?.role === 'sys_admin' || currentUser?.role === 'company_manager';

  const userRoleLabel = useMemo(() => {
    switch (currentUser?.role) {
      case 'sys_admin':
        return t('System Admin', 'מנהל מערכת');
      case 'company_manager':
        return t('Practice Manager', 'מנהל קליניקה');
      case 'employee':
        return t('Staff Specialist', 'צוות מקצועי');
      default:
        return t('Staff Member', 'איש צוות');
    }
  }, [currentUser?.role, language]);

  const userName =
    currentUser?.full_name || currentUser?.name || currentUser?.email?.split('@')[0] || t('Account', 'חשבון');

  const companyLabel = useMemo(() => {
    const name = String(currentUser?.company_name || '').trim();
    if (!name || name.toLowerCase() === 'betterchoice') return '';
    return name;
  }, [currentUser?.company_name]);

  const userInitials = useMemo(() => {
    const name = (currentUser?.full_name || currentUser?.name || '').trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    const email = currentUser?.email || '';
    if (email) return email.slice(0, 2).toUpperCase();
    return 'BC';
  }, [currentUser]);

  const handleLogout = async () => {
    localStorage.removeItem('selectedClient');
    localStorage.removeItem('selectedClientId');
    await authClient.signOut();
    navigate('/');
  };

  const handleReplayTour = () => {
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
    window.dispatchEvent(new CustomEvent('portfolio-playbook-reopen'));
  };

  const isOverviewActive = location.pathname === '/dietitian-dashboard' || location.pathname === '/overview';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 w-full h-14 sm:h-16 xl:h-[4.5rem] border-b border-rule transition-colors duration-200 backdrop-blur-xl ${
        isDarkMode ? 'bg-canvas-dark/95' : 'bg-white/95'
      }`}
      dir={direction}
      aria-label={t('Primary Navigation', 'ניווט ראשי')}
    >
      <div className="max-w-7xl mx-auto h-full px-3 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
        
        {/* LEFT: Logo & Brand Name */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/dietitian-dashboard"
            className="flex min-h-11 min-w-11 items-center justify-center gap-2.5 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-forest rounded-xl"
            aria-label={t('BetterChoice Practice Overview', 'BetterChoice סקירת קליניקה')}
          >
            {/* BetterChoice lockup from the brand kit: the mark alone on small screens. */}
            <BrandMark size={30} className="sm:hidden text-forest-ondark dark:text-forest" />
            <span className="hidden sm:inline-flex items-center text-ink dark:text-white">
              <BrandLockup height={26} decorative />
            </span>
          </Link>
          {companyLabel ? (
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="hidden sm:block h-4 w-px shrink-0 bg-line dark:bg-line-dark" aria-hidden="true" />
              <span
                className="max-w-[7rem] sm:max-w-[11rem] truncate text-[13px] font-semibold text-ink dark:text-ink-dark"
                title={companyLabel}
              >
                {companyLabel}
              </span>
            </span>
          ) : null}
        </div>

        {/* CENTER: Practice-level navigation links only */}
        <div className="hidden lg:flex items-center gap-1 p-1 rounded-full border border-rim bg-canvas/60 dark:bg-surface-dark/70">
          
          {/* Overview (Roster & KPIs) */}
          <Link
            to="/dietitian-dashboard"
            aria-current={isOverviewActive ? 'page' : undefined}
            className={`px-4 py-2 text-xs xl:text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap ${
              isOverviewActive
                ? 'bg-forest text-white shadow-sm'
                : 'text-ink-muted dark:text-ink-muted-dark hover:text-ink dark:hover:text-white hover:bg-white dark:hover:bg-moss-dark/60'
            }`}
          >
            {t('Overview', 'סקירה כללית')}
          </Link>

          {/* Personal client link (every staff role) */}
          <Link
            to="/my-link"
            aria-current={location.pathname === '/my-link' ? 'page' : undefined}
            className={`px-4 py-2 text-xs xl:text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap ${
              location.pathname === '/my-link'
                ? 'bg-forest text-white'
                : 'text-ink-muted dark:text-ink-muted-dark hover:text-ink dark:hover:text-white hover:bg-white dark:hover:bg-moss-dark/60'
            }`}
          >
            {t('My link', 'הקישור שלי')}
          </Link>

          {/* Management destinations (Managers & Admins only) — split out, no dropdown */}
          {isManagerOrAdmin && (
            <div data-tour="playbook-topbar-company-menu" className="flex items-center gap-1">
              {[
                { to: '/team', label: t('Coaches & links', 'מאמנים וקישורים'), tour: undefined },
                { to: '/employee-management', label: t('Team & Invites', 'צוות והזמנות'), tour: 'playbook-topbar-company-team' },
                { to: '/manager-dashboard', label: t('Pricing & Signup', 'תמחור והרשמה'), tour: 'playbook-topbar-company-pricing' },
                { to: '/landing-page-builder', label: t('Landing Page', 'דף נחיתה'), tour: 'playbook-topbar-company-landing' },
              ].map(({ to, label, tour }) => {
                const active = location.pathname === to;
                return (
                  <Link
                    key={to}
                    to={to}
                    data-tour={tour}
                    aria-current={active ? 'page' : undefined}
                    className={`px-4 py-2 text-xs xl:text-sm font-semibold rounded-full transition-all duration-150 whitespace-nowrap ${
                      active
                        ? 'bg-forest text-white'
                        : 'text-ink-muted dark:text-ink-muted-dark hover:text-ink dark:hover:text-white hover:bg-white dark:hover:bg-moss-dark/60'
                    }`}
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          )}

        </div>

        {/* RIGHT: Notifications Bell + Account Identity Menu */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">

          {/* Notifications Bell */}
          <Link
            to="/notifications"
            data-tour="playbook-topbar-notifications"
            className={`relative p-2 sm:p-2.5 rounded-full border border-rim shadow-sm transition-all duration-150 active:scale-95 ${
              location.pathname === '/notifications'
                ? 'bg-forest text-white'
                : isDarkMode
                ? 'bg-surface-dark text-ink-muted-dark hover:text-white hover:bg-moss-dark'
                : 'bg-white text-ink-muted hover:text-ink hover:bg-canvas'
            }`}
            title={t('Notifications Center', 'מרכז התראות')}
            aria-label={t('Notifications Center', 'מרכז התראות')}
          >
            <Bell size={18} />
          </Link>

          {/* Account Menu Trigger */}
          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => setIsAccountMenuOpen((prev) => !prev)}
              aria-expanded={isAccountMenuOpen}
              aria-haspopup="true"
              className={`flex items-center gap-2 ps-1.5 pe-2.5 sm:ps-2 sm:pe-3.5 py-1 rounded-full border border-rim shadow-sm transition-all duration-150 active:scale-95 ${
                isDarkMode ? 'bg-surface-dark hover:border-forest text-white' : 'bg-white hover:border-forest text-ink'
              }`}
            >
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-forest-faint dark:bg-forest/20 text-forest dark:text-forest-ondark flex items-center justify-center text-xs sm:text-sm font-bold border border-forest/20">
                {userInitials}
              </div>
              <div className="hidden sm:flex flex-col text-start leading-tight">
                <span className="text-xs font-bold truncate max-w-[120px]">{userName}</span>
                <span className="text-[10px] text-ink-muted dark:text-ink-muted-dark font-medium truncate max-w-[120px]">
                  {userRoleLabel}
                </span>
              </div>
              <ChevronDown
                size={14}
                className={`opacity-50 transition-transform duration-200 ${isAccountMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Account Popover Menu */}
            {isAccountMenuOpen && (
              <div
                className={`absolute top-full ${direction === 'rtl' ? 'left-0' : 'right-0'} mt-2 w-72 rounded-2xl shadow-2xl border border-rim p-2 z-[110] animate-in fade-in duration-120 ${
                  isDarkMode ? 'bg-surface-dark text-white' : 'bg-white text-ink'
                }`}
              >
                {/* User Info Header */}
                <div className="p-3 border-b border-rule">
                  <p className="text-sm font-bold text-ink dark:text-white truncate">{userName}</p>
                  {currentUser?.email && (
                    <p className="text-xs text-ink-muted dark:text-ink-muted-dark truncate mt-0.5">
                      {currentUser.email}
                    </p>
                  )}
                  <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-forest-faint dark:bg-forest/20 text-forest dark:text-forest-ondark border border-forest/20">
                    {userRoleLabel}
                  </span>
                </div>

                {/* Settings & Preferences */}
                <div className="py-1">
                  {/* Theme Switcher */}
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-canvas dark:hover:bg-moss-dark/40 transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      {isDarkMode ? <Sun size={16} className="text-amber-400" /> : <Moon size={16} className="text-ink-muted" />}
                      <span>{t('Theme', 'ערכת נושא')}</span>
                    </div>
                    <span className="text-xs text-ink-muted dark:text-ink-muted-dark font-semibold">
                      {isDarkMode ? t('Dark', 'כהה') : t('Light', 'בהיר')}
                    </span>
                  </button>

                  {/* Language Switcher */}
                  <button
                    type="button"
                    onClick={toggleLanguage}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-canvas dark:hover:bg-moss-dark/40 transition-colors"
                  >
                    <div className="flex items-center gap-2.5">
                      <Globe size={16} className="text-forest" />
                      <span>{t('Language', 'שפה')}</span>
                    </div>
                    <span className="text-xs font-bold text-forest dark:text-forest-ondark">
                      {language === 'english' ? 'עברית' : 'English'}
                    </span>
                  </button>

                  {/* Replay Tour */}
                  <button
                    type="button"
                    onClick={handleReplayTour}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-medium hover:bg-canvas dark:hover:bg-moss-dark/40 transition-colors text-ink dark:text-white"
                  >
                    <Sparkles size={16} className="text-forest-ondark dark:text-forest" />
                    <span>{t('Replay Tour', 'הפעלת הסיור מחדש')}</span>
                  </button>
                </div>

                <div className="h-px bg-rule my-1" />

                {/* Sign Out */}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                >
                  <LogOut size={16} />
                  <span>{t('Sign Out', 'התנתקות')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Mobile Hamburger Menu Button */}
          <button
            type="button"
            data-tour="playbook-topbar-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-full border border-rim shadow-sm transition-colors active:scale-95 ${
              isDarkMode ? 'bg-surface-dark text-white' : 'bg-white text-ink'
            }`}
            aria-label={t('Toggle mobile menu', 'תפריט')}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

        </div>
      </div>

      {/* MOBILE DRAWER */}
      {isMobileMenuOpen && (
        <div
          className={`lg:hidden fixed inset-x-0 top-14 sm:top-16 border-b border-rule shadow-2xl z-40 transition-all p-4 ${
            isDarkMode ? 'bg-canvas-dark text-white' : 'bg-white text-ink'
          }`}
        >
          <div className="space-y-3 max-h-[calc(100vh-5rem)] overflow-y-auto">
            
            {/* Primary Nav Links */}
            <div className="space-y-1">
              <Link
                to="/dietitian-dashboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  isOverviewActive
                    ? 'bg-forest text-white'
                    : 'hover:bg-canvas dark:hover:bg-surface-dark text-ink dark:text-white'
                }`}
              >
                {t('Overview', 'סקירה כללית')}
              </Link>

              <Link
                to="/my-link"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                  location.pathname === '/my-link'
                    ? 'bg-forest text-white'
                    : 'hover:bg-canvas dark:hover:bg-surface-dark text-ink dark:text-white'
                }`}
              >
                {t('My link', 'הקישור שלי')}
              </Link>

              {isManagerOrAdmin && (
                <div className="pt-2" data-tour="playbook-topbar-company-menu">
                  <div className="px-4 py-1 text-xs font-bold uppercase tracking-wider text-ink-muted">
                    {t('Management', 'ניהול')}
                  </div>
                  <div className="space-y-1 mt-1">
                    <Link
                      to="/team"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-2 rounded-lg text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark"
                    >
                      {t('Coaches & links', 'מאמנים וקישורים')}
                    </Link>
                    <Link
                      to="/employee-management"
                      data-tour="playbook-topbar-company-team"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-2 rounded-lg text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark"
                    >
                      {t('Team & Invites', 'צוות והזמנות')}
                    </Link>
                    <Link
                      to="/manager-dashboard"
                      data-tour="playbook-topbar-company-pricing"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-2 rounded-lg text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark"
                    >
                      {t('Pricing & Signup', 'תמחור והרשמה')}
                    </Link>
                    <Link
                      to="/landing-page-builder"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-2 rounded-lg text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark"
                    >
                      {t('Landing Page', 'דף נחיתה')}
                    </Link>
                  </div>
                </div>
              )}

              <Link
                to="/notifications"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-canvas dark:hover:bg-surface-dark"
              >
                {t('Notifications', 'התראות')}
              </Link>

            </div>

            <div className="h-px bg-rule my-2" />

            {/* Mobile Preferences & Logout */}
            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={toggleTheme}
                className="px-4 py-2 rounded-xl text-xs font-semibold border border-rule flex items-center gap-2"
              >
                {isDarkMode ? <Sun size={14} className="text-amber-400" /> : <Moon size={14} className="text-ink-muted" />}
                <span>{isDarkMode ? t('Light Mode', 'מצב בהיר') : t('Dark Mode', 'מצב כהה')}</span>
              </button>

              <button
                type="button"
                onClick={toggleLanguage}
                className="px-4 py-2 rounded-xl text-xs font-bold border border-rule text-forest dark:text-forest-ondark"
              >
                {language === 'english' ? 'עברית' : 'English'}
              </button>

              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300"
              >
                {t('Sign Out', 'התנתקות')}
              </button>
            </div>

          </div>
        </div>
      )}
    </nav>
  );
}
